import { AnimationGroup, Color3, Mesh, MeshBuilder, Scene, StandardMaterial, Texture, TransformNode, Vector3 } from "@babylonjs/core";
import type { GameAssetManager } from "../core/AssetManager";
import { shortestYawStep, wrappedAngleDifference, yawFromVelocity } from "./GhostFacing";
import { GhostState } from "./GhostStateMachine";

export const TOYOL_ASSET_PATH = "./assets/ghosts/Toyol.glb";
export const TOYOL_TARGET_HEIGHT = 0.96;
export const TOYOL_FORWARD_YAW = 0;
export const TOYOL_TURN_SPEED = Math.PI * 2.5;
export const TOYOL_EMISSIVE_INTENSITY = 0.06;
export const TOYOL_TEXTURE_ANISOTROPY = 8;
export const TOYOL_REQUIRED_CLIPS = ["Running", "Walking", "Attack", "Unsteady_Walk"] as const;
export type ToyolAnimationClip = typeof TOYOL_REQUIRED_CLIPS[number];

const ATTACK_SECONDS = 2.8;
const STAGGER_SECONDS = 1.22;

export class ToyolVisual {
  readonly root: TransformNode;
  readonly facingRoot: TransformNode;
  readonly collider: Mesh;
  readonly ready: Promise<boolean>;
  readonly baitAttachment: TransformNode;
  private readonly animationRoot: TransformNode;
  private readonly placeholder: Mesh;
  private readonly groups = new Map<ToyolAnimationClip, AnimationGroup>();
  private readonly weights = new Map<ToyolAnimationClip, number>();
  private attackRemaining = 0;
  private staggerRemaining = 0;
  private measuredHeight = TOYOL_TARGET_HEIGHT;
  private targetYaw = 0;
  private actualMovement = Vector3.Zero();
  private actualSpeed = 0;

  constructor(scene: Scene, assets: GameAssetManager) {
    this.root = new TransformNode("toyol-root", scene);
    this.facingRoot = new TransformNode("toyol-facing-root", scene); this.facingRoot.parent = this.root;
    this.animationRoot = new TransformNode("toyol-animation-root", scene); this.animationRoot.parent = this.facingRoot;
    this.baitAttachment = new TransformNode("toyol-bait-attachment", scene); this.baitAttachment.parent = this.facingRoot;
    this.baitAttachment.position.set(.24, TOYOL_TARGET_HEIGHT * .7, .12);

    this.placeholder = MeshBuilder.CreateCapsule("toyol-loading-placeholder", { height: TOYOL_TARGET_HEIGHT, radius: .22, tessellation: 8 }, scene);
    this.placeholder.parent = this.animationRoot; this.placeholder.position.y = TOYOL_TARGET_HEIGHT * .5;
    const material = new StandardMaterial("toyol-placeholder-material", scene);
    material.diffuseColor = new Color3(.18, .48, .2); material.emissiveColor = new Color3(.01, .02, .01);
    this.placeholder.material = material; this.placeholder.isPickable = false;

    this.collider = MeshBuilder.CreateCapsule("toyol-capture-collider", { height: TOYOL_TARGET_HEIGHT, radius: .25, tessellation: 8 }, scene);
    this.collider.parent = this.root; this.collider.position.y = TOYOL_TARGET_HEIGHT * .5;
    this.collider.visibility = 0; this.collider.isPickable = true; this.collider.metadata = { ghost: true, ghostId: "toyol" };
    for (const clip of TOYOL_REQUIRED_CLIPS) this.weights.set(clip, 0);
    this.ready = this.loadModel(assets);
  }

  get height(): number { return this.measuredHeight; }
  get animationClip(): ToyolAnimationClip | "loading" {
    let selected:ToyolAnimationClip|"loading"="loading",maximum=0;
    for(const [clip,weight] of this.weights)if(weight>maximum){maximum=weight;selected=clip;}
    return selected;
  }
  get movementDiagnostics(){return{movement:this.actualMovement.clone(),speed:this.actualSpeed,targetYaw:this.targetYaw,visualYaw:this.facingRoot.rotation.y,facingError:wrappedAngleDifference(this.facingRoot.rotation.y,this.targetYaw),modelOffset:TOYOL_FORWARD_YAW,animationClip:this.animationClip,rootMotionTracks:0};}

  update(deltaSeconds: number, state: GhostState): void {
    this.attackRemaining=Math.max(0,this.attackRemaining-deltaSeconds);
    this.staggerRemaining=Math.max(0,this.staggerRemaining-deltaSeconds);
    const fast=[GhostState.ESCAPING,GhostState.AGGRESSIVE].includes(state);
    const target:ToyolAnimationClip=this.staggerRemaining>0?"Unsteady_Walk":this.attackRemaining>0?"Attack":fast?"Running":"Walking";
    const alpha=1-Math.exp(-Math.max(0,deltaSeconds)*8);
    for(const clip of TOYOL_REQUIRED_CLIPS){const next=this.weights.get(clip)!+(Number(clip===target)-this.weights.get(clip)!)*alpha;this.weights.set(clip,next);this.groups.get(clip)?.setWeightForAllAnimatables(next);}
    this.collider.position.y=this.measuredHeight*.5;
  }

  hit(): void { this.staggerRemaining=STAGGER_SECONDS;this.attackRemaining=0;this.restartOneShot("Unsteady_Walk",2.4); }
  slip():void{this.hit();}
  attack():void{if(this.staggerRemaining>0)return;this.attackRemaining=ATTACK_SECONDS;this.restartOneShot("Attack",1);}

  faceActualMovement(movement:Vector3,deltaSeconds:number):void{const horizontal=new Vector3(movement.x,0,movement.z);this.actualMovement.copyFrom(horizontal);this.actualSpeed=deltaSeconds>0?horizontal.length()/deltaSeconds:0;if(horizontal.lengthSquared()<.0000001)return;this.targetYaw=yawFromVelocity(horizontal);this.facingRoot.rotation.y=shortestYawStep(this.facingRoot.rotation.y,this.targetYaw,deltaSeconds*TOYOL_TURN_SPEED);}
  lookToward(target:Vector3,amount=.12):void{this.targetYaw=Math.atan2(target.x-this.root.position.x,target.z-this.root.position.z);this.facingRoot.rotation.y=shortestYawStep(this.facingRoot.rotation.y,this.targetYaw,amount);}

  private restartOneShot(clip:ToyolAnimationClip,speedRatio:number):void{const group=this.groups.get(clip);if(!group)return;group.stop();group.start(false,speedRatio);group.setWeightForAllAnimatables(1);this.weights.set(clip,1);}

  private async loadModel(assets:GameAssetManager):Promise<boolean>{
    const model=await assets.instantiate(TOYOL_ASSET_PATH,"toyol-model",{scale:TOYOL_TARGET_HEIGHT,normalizeBy:"height",rotation:new Vector3(0,TOYOL_FORWARD_YAW,0)});
    if(!model||this.root.isDisposed()){model?.dispose();return false;}
    model.parent=this.animationRoot;
    const modelHeight=(model.metadata as {modelBounds?:{height?:number}}|null)?.modelBounds?.height;
    if(typeof modelHeight==="number"&&modelHeight>.75&&modelHeight<1.2)this.measuredHeight=modelHeight;
    const sourceGroups=((model.metadata as {animationGroups?:AnimationGroup[]}|null)?.animationGroups??[]);
    for(const group of sourceGroups){
      const clip=TOYOL_REQUIRED_CLIPS.find((name)=>group.name===name||group.name.endsWith(`-${name}`));if(!clip)continue;
      for(const targeted of [...group.targetedAnimations]){const targetName=(targeted.target as {name?:string}|undefined)?.name??"";if(targetName.endsWith("mixamorigHips")&&targeted.animation.targetProperty==="position")group.removeTargetedAnimation(targeted.animation);}
      this.groups.set(clip,group);group.start(true);group.setWeightForAllAnimatables(0);
    }
    for(const mesh of model.getChildMeshes(false)){
      mesh.isPickable=false;mesh.metadata={...mesh.metadata,ghostVisual:true,ghostId:"toyol"};
      const material=mesh.material as {emissiveColor?:Color3;emissiveIntensity?:number;albedoTexture?:Texture|null;emissiveTexture?:Texture|null}|null;
      if(material?.emissiveColor)material.emissiveColor=Color3.White().scale(TOYOL_EMISSIVE_INTENSITY);
      if(material&&"emissiveIntensity" in material)material.emissiveIntensity=TOYOL_EMISSIVE_INTENSITY;
      for(const texture of [material?.albedoTexture,material?.emissiveTexture])if(texture){texture.updateSamplingMode(Texture.TRILINEAR_SAMPLINGMODE);texture.anisotropicFilteringLevel=TOYOL_TEXTURE_ANISOTROPY;}
    }
    this.placeholder.dispose();
    return TOYOL_REQUIRED_CLIPS.every((clip)=>this.groups.has(clip));
  }
}
